import { useMemo, useContext, useState, FC } from 'react';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { differenceInSeconds } from 'date-fns';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
  associateEventsActions,
  AuthenticationContext,
  chatActions,
  ChatType,
  ConversationRequestEvent,
  getHeroConversationById,
  notificationsActions,
  useAppDispatch,
  useClaimConversation,
  useDeclineAssociateEvent,
  useGetAssociateSettings,
} from '@vs-online-only-app/state';
import { CountdownTimer, IconChat } from '@vs-online-only-app/ui';
import * as Sentry from '@sentry/react';
import { AnimatedBaseConversationCard } from './StyledConversationCards';
import { ShopperBadge } from './ShopperBadge';
import { ParticipantName } from './ParticipantName';
import { useNavigate } from 'react-router-dom';
import useConversationExpiryTime from '../../hooks/useConversationRequestExpiryTime';

const Body = styled(Stack)`
  padding: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.requestNotification.main};
  gap: ${({ theme }) => theme.spacing(2)};
`;

const Footer = styled(Stack)`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(1.5, 0)};
  gap: ${({ theme }) => theme.spacing(0.5)};
`;

export const OnlineStatusIndicator = styled.div<{ $isOnline?: boolean }>`
  border-radius: 999px;
  width: 10px;
  height: 10px;
  background-color: ${({ theme, $isOnline }) =>
    $isOnline ? theme.palette.success.main : 'transparent'};
  border: 2px solid
    ${({ theme, $isOnline }) =>
      $isOnline ? theme.palette.success.main : theme.palette.divider};
`;

type Props = {
  event: ConversationRequestEvent;
};

const NewConversationRequestCard: FC<Props> = ({ event }) => {
  const [isVisible, setIsVisible] = useState<boolean>(true);
  const [isClaimConversationInProgress, setIsClaimConversationInProgress] =
    useState<boolean>(false);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { data: associateSettings } = useGetAssociateSettings();
  const { user: associate } = useContext(AuthenticationContext);
  const claimConversationMutation = useClaimConversation();
  const declineAssociateEventMutation = useDeclineAssociateEvent();
  const queryClient = useQueryClient();

  const { expiryTime, clearExpiryTime } = useConversationExpiryTime(event);

  const {
    id: eventId,
    conversation: {
      conversationStarter: { firstName: shopperFirstName },
      id: conversationId,
    },
    data: { numberOfAssociatesInPool: otherOnlineAssociatesCount },
  } = event;

  // The shopper is new for current associate if current associate didn't participate in conversation
  const isNewShopper = useMemo(
    () =>
      !event.conversation.participants.find((participant) => {
        return participant.id === associate?.id;
      }),
    [event.conversation.participants, associate],
  );

  const claimConversation = async () => {
    if (!associate || !associateSettings) return;

    clearExpiryTime();
    setIsClaimConversationInProgress(true);
    dispatch(associateEventsActions.setCurrentIncomingConversationRequestEvent(undefined));

    try {
      await claimConversationMutation.mutateAsync({
        associateId: associate.id,
        conversationId,
      });
      const conversation = await getHeroConversationById(conversationId);
      if (conversation) {
        queryClient.invalidateQueries('getActiveHeroConversations');
        navigate(`/chats?id=${conversationId}`);
        dispatch(chatActions.setSelectedConversation(conversation));
        dispatch(chatActions.setSelectedChatType(ChatType.Active));
      }
    } catch (e) {
      console.warn('Unable to claim conversation', e);
      Sentry.withScope((scope) => {
        scope.setTags({
          source: 'NewConversationRequestCard.claimConversation',
        });
        Sentry.captureException(e, {
          level: 'warning',
        });
      });
      setIsClaimConversationInProgress(false);
      dispatch(
        notificationsActions.setCurrentNotification({
          message: 'Unable to claim conversation, please try again.',
          severity: 'error',
        }),
      );
    }
  };

  const remainingDurationSeconds = () => {
    if (isClaimConversationInProgress || !expiryTime) {
      return 0;
    }

    return differenceInSeconds(new Date(expiryTime), new Date(), {
      roundingMethod: 'ceil',
    });
  };

  const handleTimeout = () => {
    setIsVisible(false);
    clearExpiryTime();
    // Even thus this is the current event and the actual timeout event comes later, we clear it together when hiding
    // the UI to sync the application logic
    dispatch(associateEventsActions.setCurrentIncomingConversationRequestEvent(undefined));
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatedBaseConversationCard>
      <Body>
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Stack gap={1}>
            <ShopperBadge type={isNewShopper ? 'new' : 'familiar'} />
            <Typography>
              <strong>
                <ParticipantName name={shopperFirstName} />
              </strong>{' '}
              is waiting to chat...
            </Typography>
          </Stack>
          {associateSettings && (
            <CountdownTimer
              isPaused={isClaimConversationInProgress}
              onTimeout={handleTimeout}
              remainingDurationSeconds={remainingDurationSeconds()}
              initialDurationSeconds={associateSettings.settings.requestTimer}
            />
          )}
        </Stack>
        <Stack direction="row" gap={1}>
          {associate && (
            <Button
              variant="contained"
              startIcon={!isClaimConversationInProgress && <IconChat />}
              onClick={claimConversation}
              sx={{ minWidth: '50%', textWrap: 'nowrap' }}
            >
              {isClaimConversationInProgress ? (
                <CircularProgress color="inherit" size={20} />
              ) : (
                'Claim and chat'
              )}
            </Button>
          )}
          <Button
            variant="outlined"
            disabled={isClaimConversationInProgress}
            onClick={() => {
              handleTimeout();
              declineAssociateEventMutation.mutateAsync({
                eventId,
                goUnavailable: false,
              });
            }}
          >
            Decline
          </Button>
        </Stack>
      </Body>
      <Footer>
        <OnlineStatusIndicator $isOnline />
        <Typography color="textSecondary" variant="body2">
          Also online:
        </Typography>
        <Typography variant="body2">
          {otherOnlineAssociatesCount} team{' '}
          {otherOnlineAssociatesCount === 1 ? 'member' : 'members'}
        </Typography>
      </Footer>
    </AnimatedBaseConversationCard>
  );
};

export default NewConversationRequestCard;
